/**
 * Regions Section Component
 * Smart component - wires up Redux actions to dumb components
 * Only accessible to super admins
 */

import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { useSelector, useDispatch, useTranslation, useIsRTL } from '@spcc/core'
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  Plus,
  Search,
  MapPin,
  ShieldAlert,
} from '@spcc/uikit'

import {
  selectRegions,
  selectRegionStats,
  selectRegionsLoading,
  selectRegionsError,
  selectRegionsFilter,
  selectAvailableFactories,
  selectAvailableCountries,
  selectSelectedRegion,
} from '../../store/regions.selectors'
import { regionsActions } from '../../store/regions.slice'
import type {
  RegionListItem,
  Region,
  CreateRegionRequest,
  UpdateRegionRequest,
} from '../../domain/types'
import { RegionCard } from '../cards'
import type { RegionFormSubmitData } from '../drawers'
const RegionFormDrawer = lazy(() => import('../drawers/region-form-drawer').then(m => ({ default: m.RegionFormDrawer })))
const RegionDetailDrawer = lazy(() => import('../drawers/region-detail-drawer').then(m => ({ default: m.RegionDetailDrawer })))
const DeleteRegionDialog = lazy(() => import('../modals').then(m => ({ default: m.DeleteRegionDialog })))

// Import auth selector for admin check
import { selectIsAdmin, selectUserRoleType, isLocationConfigReadOnlyRole } from '@spcc/domains/auth'

// ============================================================================
// Component
// ============================================================================

export function RegionsSection() {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const isArabic = i18n.language === 'ar' // Keep for content selection (Arabic text vs English)

  // Redux state
  const regions = useSelector(selectRegions)
  const stats = useSelector(selectRegionStats)
  const loading = useSelector(selectRegionsLoading)
  const error = useSelector(selectRegionsError)
  const filter = useSelector(selectRegionsFilter)
  const factories = useSelector(selectAvailableFactories)
  const countries = useSelector(selectAvailableCountries)
  const selectedRegion = useSelector(selectSelectedRegion)
  const isAdmin = useSelector(selectIsAdmin)
  const roleType = useSelector(selectUserRoleType)
  const isReadOnly = isLocationConfigReadOnlyRole(roleType)

  // Local state
  const [searchValue, setSearchValue] = useState(filter.search || '')
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [regionToEdit, setRegionToEdit] = useState<Region | null>(null)
  const [regionToDelete, setRegionToDelete] = useState<RegionListItem | null>(null)

  // Fetch data on mount
  useEffect(() => {
    dispatch(regionsActions.fetchRegions())
    dispatch(regionsActions.fetchRegionStats())
    dispatch(regionsActions.fetchAvailableFactories())
    dispatch(regionsActions.fetchAvailableCountries())
  }, [dispatch])

  useEffect(() => {
    setSearchValue(filter.search || '')
  }, [filter.search])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value)
      dispatch(regionsActions.setFilter({ search: value }))
    },
    [dispatch]
  )

  const handleStatusFilter = useCallback(
    (status: string) => {
      const isActive = status === 'all' ? 'all' : status === 'active'
      dispatch(regionsActions.setFilter({ isActive }))
    },
    [dispatch]
  )

  const handleAddClick = useCallback(() => {
    setRegionToEdit(null)
    setIsFormDrawerOpen(true)
  }, [])

  const handleCardSelect = useCallback(
    (region: RegionListItem) => {
      dispatch(regionsActions.fetchRegionDetail(region.documentId))
      setIsDetailDrawerOpen(true)
    },
    [dispatch]
  )

  const handleCardSettings = useCallback(
    (region: RegionListItem) => {
      dispatch(regionsActions.fetchRegionDetail(region.documentId))
      setIsFormDrawerOpen(true)
    },
    [dispatch]
  )

  // Set regionToEdit when selectedRegion changes (after fetch)
  useEffect(() => {
    if (selectedRegion && isFormDrawerOpen) {
      setRegionToEdit(selectedRegion)
    }
  }, [selectedRegion, isFormDrawerOpen])

  const handleEditFromDetail = useCallback((region: Region) => {
    setRegionToEdit(region)
    setIsDetailDrawerOpen(false)
    setIsFormDrawerOpen(true)
  }, [])

  const handleDeleteFromDetail = useCallback((region: Region) => {
    // Convert Region to RegionListItem for delete dialog
    const regionListItem: RegionListItem = {
      id: region.id,
      documentId: region.documentId,
      code: region.code,
      name: region.name,
      description: region.description,
      isActive: region.isActive,
      countryId: region.country?.id,
      countryCode: region.country?.code,
      countryName: region.country?.name,
      factoryCount: region.factories?.length ?? 0,
      factoryNames: region.factories?.map((f) => f.name),
      cityCount: region.cities?.length ?? 0,
      createdAt: region.createdAt,
      updatedAt: region.updatedAt,
    }
    setRegionToDelete(regionListItem)
    setIsDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (regionToDelete) {
      dispatch(regionsActions.deleteRegion(regionToDelete.documentId))
      setIsDeleteDialogOpen(false)
      setIsDetailDrawerOpen(false)
      setRegionToDelete(null)
    }
  }, [dispatch, regionToDelete])

  const handleFormSubmit = useCallback(
    (data: RegionFormSubmitData) => {
      if (regionToEdit) {
        // Update existing region
        const updateRequest: UpdateRegionRequest = {
          name: data.name,
          description: data.description,
          isActive: data.isActive,
          countryId: data.countryId,
          factoryIds: data.factoryIds,
        }
        dispatch(
          regionsActions.updateRegion({
            documentId: regionToEdit.documentId,
            data: updateRequest,
          })
        )
      } else {
        // Create new region
        const createRequest: CreateRegionRequest = {
          code: data.code,
          name: data.name,
          description: data.description,
          isActive: data.isActive,
          countryId: data.countryId,
          factoryIds: data.factoryIds,
        }
        dispatch(regionsActions.createRegion(createRequest))
      }
      setIsFormDrawerOpen(false)
      setRegionToEdit(null)
    },
    [dispatch, regionToEdit]
  )

  const handleFormDrawerClose = useCallback(
    (open: boolean) => {
      setIsFormDrawerOpen(open)
      if (!open) {
        setRegionToEdit(null)
        dispatch(regionsActions.setSelectedRegion(null))
      }
    },
    [dispatch]
  )

  const handleDetailDrawerClose = useCallback(
    (open: boolean) => {
      setIsDetailDrawerOpen(open)
      if (!open) {
        dispatch(regionsActions.setSelectedRegion(null))
      }
    },
    [dispatch]
  )

  const handleRetry = useCallback(() => {
    dispatch(regionsActions.fetchRegions())
  }, [dispatch])

  // ============================================================================
  // Filtering
  // ============================================================================

  // Apply client-side filter
  const filteredRegions = regions.filter((region) => {
    // Status filter
    if (filter.isActive !== 'all' && region.isActive !== filter.isActive) {
      return false
    }
    // Search filter
    if (filter.search) {
      const searchLower = filter.search.toLowerCase()
      const matchesName = region.name.toLowerCase().includes(searchLower)
      const matchesCode = region.code.toLowerCase().includes(searchLower)
      if (!matchesName && !matchesCode) {
        return false
      }
    }
    return true
  })

  // ============================================================================
  // Render
  // ============================================================================

  // Access denied - not an admin (IT has read-only access)
  if (!isAdmin && !isReadOnly) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-amber-500 mb-4">
          <ShieldAlert className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('common.accessDenied', 'Access Denied')}
        </h3>
        <p className="text-muted-foreground max-w-md">
          {isArabic
            ? 'إدارة المناطق مقيدة لمسؤولي النظام. يرجى التواصل مع المسؤول إذا كنت بحاجة إلى الوصول.'
            : 'Region management is restricted to system administrators. Please contact your administrator if you need access.'}
        </p>
      </div>
    )
  }

  // Loading state
  if (loading && regions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  // Error state
  if (error && regions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="text-destructive mb-4">
          <MapPin className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={handleRetry}>{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={cn('flex items-center justify-between flex-wrap gap-4')}>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('regions.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isArabic
              ? `${stats.total} منطقة • ${stats.active} نشطة • ${stats.inactive} غير نشطة`
              : `${stats.total} regions • ${stats.active} active • ${stats.inactive} inactive`}
          </p>
        </div>
        {!isReadOnly && (
          <Button onClick={handleAddClick}>
            <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
            {t('regions.addRegion')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('regions.totalRegions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('regions.activeRegions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('regions.inactiveRegions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{stats.inactive}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className={cn('flex flex-col sm:flex-row gap-4')}>
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('regions.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            className="ps-10"
          />
        </div>
        <Select
          value={filter.isActive === 'all' ? 'all' : filter.isActive ? 'active' : 'inactive'}
          onValueChange={handleStatusFilter}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder={t('regions.filter.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('regions.filter.all')}</SelectItem>
            <SelectItem value="active">{t('regions.filter.active')}</SelectItem>
            <SelectItem value="inactive">{t('regions.filter.inactive')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Regions Grid */}
      {filteredRegions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredRegions.map((region) => (
            <RegionCard
              key={region.documentId}
              region={region}
              onSelect={handleCardSelect}
              onSettings={isReadOnly ? undefined : handleCardSettings}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredRegions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">
            {filter.search || filter.isActive !== 'all'
              ? (isArabic ? 'لا توجد نتائج' : 'No results found')
              : t('regions.noRegions')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {filter.search || filter.isActive !== 'all'
              ? (isArabic ? 'جرب تغيير معايير البحث' : 'Try adjusting your search criteria')
              : t('regions.noRegionsDescription')}
          </p>
          {!filter.search && filter.isActive === 'all' && !isReadOnly && (
            <Button className="mt-4" onClick={handleAddClick}>
              <Plus className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
              {t('regions.addFirst')}
            </Button>
          )}
        </div>
      )}

      {/* Detail Drawer */}
      <Suspense fallback={null}>
        <RegionDetailDrawer
          region={selectedRegion}
          open={isDetailDrawerOpen}
          onOpenChange={handleDetailDrawerClose}
          onEdit={isReadOnly ? undefined : handleEditFromDetail}
          onDelete={isReadOnly ? undefined : handleDeleteFromDetail}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Form Drawer */}
      <Suspense fallback={null}>
        <RegionFormDrawer
          open={isFormDrawerOpen}
          onOpenChange={handleFormDrawerClose}
          region={regionToEdit}
          countries={countries}
          factories={factories}
          onSubmit={handleFormSubmit}
          isLoading={loading}
          isRTL={isRTL}
        />
      </Suspense>

      {/* Delete Confirmation Dialog */}
      {regionToDelete && (
        <Suspense fallback={null}>
          <DeleteRegionDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            regionName={regionToDelete.name}
            regionCode={regionToDelete.code}
            onConfirm={handleDeleteConfirm}
            isLoading={loading}
            isRTL={isRTL}
          />
        </Suspense>
      )}
    </div>
  )
}
